import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Pencil, Trash2, Star, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    author_name: "",
    author_role: "",
    content: "",
    rating: 5,
    is_visible: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?visible_only=false`);
      setTestimonials(response.data);
    } catch (error) {
      toast.error("Kļūda ielādējot atsauksmes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, formData);
        toast.success("Atsauksme atjaunināta");
      } else {
        await axios.post(`${API}/testimonials`, formData);
        toast.success("Atsauksme pievienota");
      }
      fetchTestimonials();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error("Kļūda saglabājot atsauksmi");
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      author_name: testimonial.author_name,
      author_role: testimonial.author_role,
      content: testimonial.content,
      rating: testimonial.rating,
      is_visible: testimonial.is_visible
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Vai tiešām vēlaties dzēst šo atsauksmi?")) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success("Atsauksme dzēsta");
      fetchTestimonials();
    } catch (error) {
      toast.error("Kļūda dzēšot atsauksmi");
    }
  };

  const toggleVisibility = async (testimonial) => {
    try {
      await axios.put(`${API}/testimonials/${testimonial.id}`, {
        is_visible: !testimonial.is_visible
      });
      toast.success(testimonial.is_visible ? "Atsauksme paslēpta" : "Atsauksme redzama");
      fetchTestimonials();
    } catch (error) {
      toast.error("Kļūda mainot redzamību");
    }
  };

  const resetForm = () => {
    setEditingTestimonial(null);
    setFormData({
      author_name: "",
      author_role: "",
      content: "",
      rating: 5,
      is_visible: true
    });
  };

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-secondary">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="section-container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Atpakaļ uz mājaslapu</span>
              </Link>
            </div>
            <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Atsauksmju pārvaldība
            </h1>
          </div>
        </div>
      </header>

      <main className="section-container py-8">
        {/* Actions */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Klientu atsauksmes
            </h2>
            <p className="text-muted-foreground mt-1">
              Kopā: {testimonials.length} atsauksmes
            </p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-testimonial-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
              >
                <Plus className="w-5 h-5 mr-2" />
                Pievienot atsauksmi
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingTestimonial ? "Rediģēt atsauksmi" : "Jauna atsauksme"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="author_name">Autora vārds</Label>
                  <Input
                    id="author_name"
                    data-testid="testimonial-author-name"
                    value={formData.author_name}
                    onChange={(e) => setFormData({...formData, author_name: e.target.value})}
                    placeholder="Piem., Jānis Bērziņš"
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author_role">Autora loma</Label>
                  <Input
                    id="author_role"
                    data-testid="testimonial-author-role"
                    value={formData.author_role}
                    onChange={(e) => setFormData({...formData, author_role: e.target.value})}
                    placeholder="Piem., Dēls, kura tēvs izmanto sistēmu"
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Atsauksmes teksts</Label>
                  <Textarea
                    id="content"
                    data-testid="testimonial-content"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Ievadiet atsauksmes tekstu..."
                    required
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vērtējums</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({...formData, rating: star})}
                        className="focus:outline-none"
                      >
                        <Star 
                          className={`w-8 h-8 transition-colors ${
                            star <= formData.rating 
                              ? 'fill-yellow-400 text-yellow-400' 
                              : 'text-gray-300 hover:text-yellow-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_visible">Redzama mājaslapā</Label>
                  <Switch
                    id="is_visible"
                    data-testid="testimonial-visibility"
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({...formData, is_visible: checked})}
                  />
                </div>
                <DialogFooter className="mt-6">
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="rounded-full">
                      Atcelt
                    </Button>
                  </DialogClose>
                  <Button 
                    type="submit" 
                    data-testid="save-testimonial-btn"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  >
                    {editingTestimonial ? "Saglabāt izmaiņas" : "Pievienot"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Testimonials List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Ielādē...</p>
          </div>
        ) : testimonials.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">Nav nevienas atsauksmes</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id} 
                data-testid={`testimonial-card-${testimonial.id}`}
                className={`${!testimonial.is_visible ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {testimonial.author_name}
                        </h3>
                        <span className="text-sm text-muted-foreground">
                          {testimonial.author_role}
                        </span>
                        {!testimonial.is_visible && (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                            Paslēpts
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 mb-3">
                        {renderStars(testimonial.rating)}
                      </div>
                      <p className="text-foreground/80">{testimonial.content}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`toggle-visibility-${testimonial.id}`}
                        onClick={() => toggleVisibility(testimonial)}
                        title={testimonial.is_visible ? "Paslēpt" : "Rādīt"}
                      >
                        {testimonial.is_visible ? (
                          <Eye className="w-5 h-5 text-primary" />
                        ) : (
                          <EyeOff className="w-5 h-5 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`edit-testimonial-${testimonial.id}`}
                        onClick={() => handleEdit(testimonial)}
                      >
                        <Pencil className="w-5 h-5 text-muted-foreground hover:text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-testimonial-${testimonial.id}`}
                        onClick={() => handleDelete(testimonial.id)}
                      >
                        <Trash2 className="w-5 h-5 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
